import React, { useState } from 'react';
import { 
  Plus, UserPlus, CheckCircle2,
  Activity, X, ChevronDown, Search, Loader2, Check
} from 'lucide-react';
import { 
  useInstallationMembers, 
  useTrackOrderData, 
  useAssignMember, 
  usePostUpdate 
} from '../hooks/use_track_order.js';
import { showSuccessMessage, showAxiosError } from '../../../../utils/toast.js';

export const TrackOrder = ({ customerId, orderId, estimationId }) => {
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [activeMemberId, setActiveMemberId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Track selected members in the modal (before saving)
  const [pendingMemberIds, setPendingMemberIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const [updateForm, setUpdateForm] = useState({
    status: 'In Progress',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    description: '',
    progress: 0,
  });

  // Data hooks
  const { data: installationMembers = [], isLoading: loadingMembers } = useInstallationMembers();
  const { data: trackOrderData, isLoading: loadingTrackOrder } = useTrackOrderData(customerId);
  const assignMemberMutation = useAssignMember(customerId);
  const postUpdateMutation = usePostUpdate(customerId);

  const assignedMembers = trackOrderData?.assignments || [];

  // Filter by search
  const filteredMembers = installationMembers.filter(m => {
    const q = searchQuery.toLowerCase();
    return m.name?.toLowerCase().includes(q) || m.role?.toLowerCase().includes(q);
  });

  const openAssignModal = () => {
    setSearchQuery('');
    setPendingMemberIds([]);
    setIsAssignModalOpen(true);
  };

  const togglePendingMember = (memberId) => {
    setPendingMemberIds(prev =>
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]
    );
  };

  const handleSaveAssignments = async () => {
    if (pendingMemberIds.length === 0) return;
    setIsSaving(true);
    let newCount = 0;
    let skipCount = 0;

    for (const memberId of pendingMemberIds) {
      try {
        const result = await assignMemberMutation.mutateAsync({
          memberId,
          orderId: orderId || undefined,
          estimationId: estimationId || undefined
        });
        if (result?.alreadyAssigned) {
          skipCount++;
        } else {
          newCount++;
        }
      } catch (err) {
        skipCount++;
      }
    }

    setIsSaving(false);
    setIsAssignModalOpen(false);
    setPendingMemberIds([]);

    if (newCount > 0) {
      showSuccessMessage(`${newCount} member${newCount > 1 ? 's' : ''} assigned successfully!`);
    } else if (skipCount > 0) {
      showSuccessMessage('Members are already assigned to this project.');
    }
  };

  const handleOpenUpdateModal = (memberId) => {
    setActiveMemberId(memberId);
    setUpdateForm({
      status: 'In Progress',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      description: '',
      progress: 0,
    });
    setIsUpdateModalOpen(true);
  };

  const handlePostUpdate = async (e) => {
    e.preventDefault();
    try {
      await postUpdateMutation.mutateAsync({
        memberId: activeMemberId,
        updateData: updateForm
      });
      setIsUpdateModalOpen(false);
      showSuccessMessage('Work update posted!');
    } catch (err) {
      showAxiosError(err, 'Failed to post update');
    }
  };

  const getAvatar = (name = '') =>
    name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="w-full min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-medium text-slate-800">Project Tracking</h2>
          <p className="text-sm text-slate-500 mt-0.5">Assign members and track their daily progress</p>
        </div>
        <button
          onClick={openAssignModal}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
        >
          <UserPlus size={16} />
          <span>Assign Member</span>
        </button>
      </div>

      {/* Members List */}
      <div className="space-y-2">
        {loadingTrackOrder ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={20} className="animate-spin text-slate-400" />
          </div>
        ) : assignedMembers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500 text-sm mb-4">No team members assigned yet.</p>
            <button
              onClick={openAssignModal}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
            >
              <UserPlus size={16} />
              <span>Assign Member</span>
            </button>
          </div>
        ) : (
          assignedMembers.map(assignment => {
            const member = assignment.memberId;
            if (!member) return null;
            const latestProgress = assignment.updates?.length > 0
              ? assignment.updates[assignment.updates.length - 1].progress
              : 0;

            return (
              <div key={assignment._id} className="py-4 border-b border-slate-100 last:border-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium text-sm">
                      {getAvatar(member.name)}
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-slate-800">{member.name}</h3>
                      <span className="text-sm text-slate-500">{member.role || '—'}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">Progress</span>
                      <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 rounded-full transition-all duration-700"
                          style={{ width: `${latestProgress}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-slate-600 w-8">{latestProgress}%</span>
                    </div>

                    <button
                      onClick={() => handleOpenUpdateModal(member._id)}
                      disabled={latestProgress === 100}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        latestProgress === 100
                          ? 'text-slate-400 cursor-not-allowed opacity-50'
                          : 'text-indigo-600 hover:bg-indigo-50 cursor-pointer'
                      }`}
                      title={latestProgress === 100 ? 'Work is 100% complete' : 'Add work update'}
                    >
                      <Plus size={16} />
                      <span>Work Update</span>
                    </button>
                  </div>
                </div>

                {/* Updates Table */}
                <div className="mt-4">
                  {assignment.updates?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                            <th className="py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Start</th>
                            <th className="py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">End</th>
                            <th className="py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                            <th className="py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Progress</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {assignment.updates.map((update, idx) => (
                            <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                              <td className="py-3 px-4">
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                                  <Activity size={11} />
                                  {update.status}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="text-sm text-slate-700">{update.startDate || '—'}</div>
                                <div className="text-xs text-slate-400">{update.startTime || ''}</div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="text-sm text-slate-700">{update.endDate || '—'}</div>
                                <div className="text-xs text-slate-400">{update.endTime || ''}</div>
                              </td>
                              <td className="py-3 px-4 max-w-xs">
                                <p className="text-sm text-slate-600 truncate" title={update.description}>
                                  {update.description || '—'}
                                </p>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <span className="text-sm font-medium text-slate-700">{update.progress}%</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 pl-1">No updates yet. Click "Work Update" to add one.</p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Assign Member Modal */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl border border-slate-100 overflow-hidden flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-base font-semibold text-slate-800">Assign Member</h3>
                {pendingMemberIds.length > 0 && (
                  <p className="text-xs text-indigo-600 mt-0.5">
                    {pendingMemberIds.length} member{pendingMemberIds.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
              <button
                onClick={() => { setIsAssignModalOpen(false); setPendingMemberIds([]); }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Search */}
            <div className="px-5 pt-4 pb-2 shrink-0">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/10 transition-all"
                  autoFocus
                />
              </div>
            </div>

            {/* Member List */}
            <div className="px-5 pb-4 flex-1 overflow-y-auto">
              {loadingMembers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={18} className="animate-spin text-slate-400" />
                </div>
              ) : filteredMembers.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  {searchQuery ? `No results for "${searchQuery}"` : 'No installation members found.'}
                </p>
              ) : (
                <div className="space-y-2 mt-2">
                  {filteredMembers.map(member => {
                    const isAlreadyAssigned = assignedMembers.find(a =>
                      String(a.memberId?._id || a.memberId) === String(member._id)
                    );
                    const isPending = pendingMemberIds.includes(String(member._id));

                    return (
                      <div
                        key={member._id}
                        onClick={() => !isAlreadyAssigned && togglePendingMember(String(member._id))}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer select-none ${
                          isAlreadyAssigned
                            ? 'border-slate-100 bg-slate-50 opacity-60 cursor-default'
                            : isPending
                            ? 'border-indigo-300 bg-indigo-50'
                            : 'border-slate-200 hover:border-indigo-200 bg-white hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-medium text-sm shrink-0 transition-colors ${
                            isPending ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {getAvatar(member.name)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{member.name}</p>
                            <p className="text-xs text-slate-500">{member.role}</p>
                          </div>
                        </div>

                        {isAlreadyAssigned ? (
                          <span className="text-xs text-slate-400 shrink-0 flex items-center gap-1">
                            <Check size={13} /> Assigned
                          </span>
                        ) : (
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                            isPending
                              ? 'bg-indigo-600 border-indigo-600'
                              : 'border-slate-300 bg-white'
                          }`}>
                            {isPending && <Check size={13} className="text-white" strokeWidth={3} />}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer — Save Assignment */}
            <div className="px-5 py-4 border-t border-slate-100 shrink-0 flex items-center justify-between gap-3">
              <button
                onClick={() => { setIsAssignModalOpen(false); setPendingMemberIds([]); }}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAssignments}
                disabled={pendingMemberIds.length === 0 || isSaving}
                className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSaving
                  ? <><Loader2 size={15} className="animate-spin" /> Saving...</>
                  : <><Check size={15} /> Save Assignment{pendingMemberIds.length > 1 ? 's' : ''} {pendingMemberIds.length > 0 ? `(${pendingMemberIds.length})` : ''}</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Work Update Modal */}
      {isUpdateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl w-full max-w-xl shadow-xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Activity className="text-indigo-600" size={18} />
                <h3 className="text-base font-semibold text-slate-800">Post Work Update</h3>
              </div>
              <button onClick={() => setIsUpdateModalOpen(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handlePostUpdate} className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Update Status</label>
                <div className="relative">
                  <select
                    required
                    value={updateForm.status}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      let newProgress = updateForm.progress;
                      if (newStatus === 'Completed') newProgress = 100;
                      else if (newStatus === 'In Progress') newProgress = 30;
                      setUpdateForm({ ...updateForm, status: newStatus, progress: newProgress });
                    }}
                    className="w-full appearance-none bg-white border border-slate-200 text-slate-700 py-2.5 px-3 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                  >
                    <option value="Started">Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Delayed">Delayed</option>
                    <option value="Completed">Completed</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={15} />
                </div>
              </div>

              {/* Timeline */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Start</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Date</label>
                      <input type="date" required value={updateForm.startDate}
                        onChange={(e) => setUpdateForm({ ...updateForm, startDate: e.target.value })}
                        className="w-full border border-slate-200 py-2 px-2.5 rounded-lg outline-none focus:border-indigo-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Time</label>
                      <input type="time" required value={updateForm.startTime}
                        onChange={(e) => setUpdateForm({ ...updateForm, startTime: e.target.value })}
                        className="w-full border border-slate-200 py-2 px-2.5 rounded-lg outline-none focus:border-indigo-500 text-sm" />
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">End</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Date</label>
                      <input type="date" required value={updateForm.endDate}
                        onChange={(e) => setUpdateForm({ ...updateForm, endDate: e.target.value })}
                        className="w-full border border-slate-200 py-2 px-2.5 rounded-lg outline-none focus:border-indigo-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Time</label>
                      <input type="time" required value={updateForm.endTime}
                        onChange={(e) => setUpdateForm({ ...updateForm, endTime: e.target.value })}
                        className="w-full border border-slate-200 py-2 px-2.5 rounded-lg outline-none focus:border-indigo-500 text-sm" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
                <textarea rows="3" required placeholder="Describe the work completed..."
                  value={updateForm.description}
                  onChange={(e) => setUpdateForm({ ...updateForm, description: e.target.value })}
                  className="w-full border border-slate-200 py-2.5 px-3 rounded-lg outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 resize-none text-sm" />
              </div>

              {/* Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Work Progress</label>
                  <span className="text-sm font-semibold text-indigo-600">{updateForm.progress}%</span>
                </div>
                <input type="range" min="0" max="100"
                  value={updateForm.progress}
                  onChange={(e) => setUpdateForm({ ...updateForm, progress: parseInt(e.target.value) })}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-indigo-600"
                  style={{ background: `linear-gradient(to right, #6366f1 ${updateForm.progress}%, #e2e8f0 ${updateForm.progress}%)` }}
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>0%</span><span>50%</span><span>100%</span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setIsUpdateModalOpen(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={postUpdateMutation.isPending}
                  className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-all disabled:opacity-60">
                  {postUpdateMutation.isPending
                    ? <><Loader2 size={15} className="animate-spin" /> Saving...</>
                    : <><CheckCircle2 size={15} /> Post Update</>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
